// 18章 塗装工事 — やさしい解説
// 原文は src/spec_index.json（PDFから自動生成）。このファイルは手書きで管理する。
// SVGは currentColor を使い、ライト/ダーク両テーマに追従させる。

import type { Commentary } from "../types";

const SVG_PAINT_FLOW = `
<svg viewBox="0 0 660 230" role="img" aria-label="塗装の基本工程。素地ごしらえで下地を整え、下塗りで密着させ、中塗りで厚みと平滑さを作り、上塗りで色とつや・耐候性を出す。素地ごしらえが仕上がりの8割を決める">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="20"  y="60" width="145" height="70" rx="8"/>
    <rect x="185" y="60" width="140" height="70" rx="8"/>
    <rect x="345" y="60" width="140" height="70" rx="8"/>
    <rect x="505" y="60" width="135" height="70" rx="8"/>
  </g>
  <g stroke="currentColor" stroke-width="1.8">
    <line x1="165" y1="95" x2="182" y2="95"/>
    <line x1="325" y1="95" x2="342" y2="95"/>
    <line x1="485" y1="95" x2="502" y2="95"/>
  </g>
  <g fill="currentColor">
    <polygon points="185,95 173,89 173,101"/>
    <polygon points="345,95 333,89 333,101"/>
    <polygon points="505,95 493,89 493,101"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="92" y="90">① 素地ごしらえ</text>
    <text x="255" y="90">② 下塗り</text>
    <text x="415" y="90">③ 中塗り</text>
    <text x="572" y="90">④ 上塗り</text>
  </g>
  <g fill="currentColor" font-size="11.5" text-anchor="middle" opacity="0.85">
    <text x="92" y="112">下地を整える</text>
    <text x="255" y="112">密着させる</text>
    <text x="415" y="112">厚み・平滑</text>
    <text x="572" y="112">色・つや・耐候</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="20" y="165" font-weight="bold">素地ごしらえ（下地処理）が塗装品質の8割を決める。</text>
    <text x="20" y="188">研磨紙ずり・穴埋め・パテかい・パテしごきで面を平らに整えてから塗る（18.2）</text>
    <text x="20" y="211" opacity="0.85">中塗り・上塗りは色を変えて塗り、塗り残しを防ぐ（18.1.4）</text>
  </g>
</svg>`;

const SVG_CONDITION = `
<svg viewBox="0 0 640 190" role="img" aria-label="塗装できない条件。気温5度以下、湿度85パーセント以上、結露のおそれがある時は塗装しない。外部は降雨のおそれや強風時は原則行わない">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="30" y="50" width="180" height="90" rx="10"/>
    <rect x="230" y="50" width="180" height="90" rx="10"/>
    <rect x="430" y="50" width="180" height="90" rx="10"/>
  </g>
  <g fill="currentColor" font-size="14" font-weight="bold" text-anchor="middle">
    <text x="120" y="90">🌡 5℃以下</text>
    <text x="320" y="90">💧 湿度85%以上</text>
    <text x="520" y="90">🌧 降雨・強風</text>
  </g>
  <g fill="currentColor" font-size="12" text-anchor="middle" opacity="0.85">
    <text x="120" y="118">乾燥・硬化不良</text>
    <text x="320" y="118">結露・つや引け</text>
    <text x="520" y="118">外部は原則中止</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="30" y="40" font-weight="bold">塗装を行わない条件（18.1.6）</text>
    <text x="30" y="170">採暖・換気を適切に行えば施工できる場合もある。溶剤中毒・火災（自然発火）にも注意</text>
  </g>
</svg>`;

const SVG_RUST = `
<svg viewBox="0 0 660 220" role="img" aria-label="鉄鋼面の錆止め塗料塗り。素地ごしらえで錆・油を除去し、錆止め塗料を2回塗る。鉄骨は1回目を工場で組立後に塗り、現場で建て方後に損傷部を補修して2回目を塗る。溶接部・接合部は錆止め2回">
  <g fill="currentColor" opacity="0.3"><rect x="60" y="120" width="540" height="30"/></g>
  <g fill="currentColor" opacity="0.5"><rect x="60" y="105" width="540" height="15"/></g>
  <g fill="currentColor" opacity="0.65"><rect x="60" y="95" width="540" height="10"/></g>
  <g stroke="currentColor" stroke-width="1.2" opacity="0.6">
    <line x1="60" y1="105" x2="600" y2="105"/><line x1="60" y1="120" x2="600" y2="120"/>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="610" y="150" text-anchor="end" opacity="0.85">鉄鋼素地（錆・油を除去）</text>
    <text x="610" y="116" text-anchor="end" opacity="0.85">錆止め1回目</text>
    <text x="610" y="103" text-anchor="end" opacity="0.85">錆止め2回目</text>
  </g>
  <g fill="currentColor" font-size="12.5">
    <text x="60" y="45" font-weight="bold">鉄鋼面の錆止め塗料塗り（18.3）</text>
    <text x="60" y="70">見え掛りA種（2回塗り＋研磨）／見え隠れB種。Ａs種＝鉛・クロムフリーさび止めペイント</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="60" y="188">鉄骨：1回目は工場で組立後に塗る。現場建て方後に損傷部・接合部を補修（未塗装部は2回）（18.3.3）</text>
    <text x="60" y="208" opacity="0.85">溶接する箇所は溶接後に塗る（先に塗ると溶接不良・有害ガスの原因）</text>
  </g>
</svg>`;

const SVG_PAINT_TYPES = `
<svg viewBox="0 0 660 240" role="img" aria-label="主な塗料の種類。合成樹脂調合ペイントSOPは油性の一般塗料、つや有合成樹脂エマルションペイントEP-Gと合成樹脂エマルションペイントEPは水性で扱いやすい、耐候性塗料DPは屋外の鉄骨等、木部はクリヤラッカーやウレタンワニス・木材保護塗料">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="20" y="55" width="200" height="150" rx="10"/>
    <rect x="230" y="55" width="200" height="150" rx="10"/>
    <rect x="440" y="55" width="200" height="150" rx="10"/>
  </g>
  <g fill="currentColor" font-size="13" font-weight="bold" text-anchor="middle">
    <text x="120" y="80">鉄・金属向け</text>
    <text x="330" y="80">コンクリ・ボード向け</text>
    <text x="540" y="80">木部向け</text>
  </g>
  <g fill="currentColor" font-size="11.5">
    <text x="34" y="106">・合成樹脂調合ペイント</text>
    <text x="46" y="122" opacity="0.8">（SOP・油性・4節）</text>
    <text x="34" y="146">・耐候性塗料（DP・7節）</text>
    <text x="46" y="162" opacity="0.8">屋外鉄骨、ふっ素等</text>
    <text x="34" y="186">・つや有EP（EP-G・8節）</text>
    <text x="244" y="106">・つや有EP（EP-G・8節）</text>
    <text x="244" y="130">・合成樹脂EP（EP・9節）</text>
    <text x="256" y="146" opacity="0.8">水性・扱いやすい</text>
    <text x="244" y="170">・アクリル非水分散（NAD）</text>
    <text x="244" y="192">・耐候性塗料（DP・7節）</text>
    <text x="454" y="106">・クリヤラッカー（CL・5節）</text>
    <text x="454" y="130">・ウレタンワニス（UC・10節）</text>
    <text x="454" y="154">・ピグメントステイン（11節）</text>
    <text x="454" y="178">・木材保護塗料（WP・12節）</text>
    <text x="466" y="194" opacity="0.8">屋外木部</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="20" y="230">素地（下地）に合った塗料を選ぶ。屋外は耐候性、水回りは耐水性、木部は透明/着色で使い分け</text>
  </g>
</svg>`;

const ch18: Record<string, Commentary> = {
  "18.1.1": {
    plainSummary:
      "この章は建築物の内外部のコンクリート・木部・金属・ボード・モルタル等の素地に塗装を施す工事に適用する。1章とセットで適用する。",
    why: "塗装は「守る（錆止め・木材保護）」と「飾る（色・つや）」の両方を担います。素地（下地）の種類ごとに適した塗料と工程が決まっており、この章はその組合せの一覧です。仕上がりは塗料より下地処理で決まります。",
    diagrams: [
      {
        svg: SVG_PAINT_FLOW,
        caption: "塗装の基本工程（18章の全体像）",
      },
    ],
  },

  "18.1.2": {
    plainSummary:
      "ゴールは3つ。①所定の材料 ②塗装の仕上り面が所要の状態 ③塗膜に耐久性・耐火性等に対する有害な欠陥がないこと。",
  },

  "18.1.3": {
    plainSummary:
      "屋内で使う塗料のホルムアルデヒド放散量は特記がなければF☆☆☆☆。防火材料は法の指定・認定品。上塗り塗料は指定の色・つやを製造所で調合し、有効期間切れは使用しない（少量は現場調色可）。副資材は上塗塗料の製造所の指定品。",
  },

  "18.1.4": {
    plainSummary:
      "塗料は調合されたものをそのまま使う（素地・気温に応じ適切な粘度に調整可）。使用直前によくかき混ぜ必要ならこし分け。研磨紙ずりは下層が硬化乾燥後に長手方向へ。穴埋め・パテかい・パテしごきで面を整える。塗り方ははけ塗り・吹付け・ローラーブラシから塗料に適したもの。中塗り・上塗りは各層の色を変えて均一に塗られていることを確認する。",
    why: "塗装の腕は「下地処理」と「塗り重ね」に出ます。パテしごき（過剰なパテをしごき取り素地が現れるまで平滑にする）で面を整え、研磨紙で平らにしてから塗る。中塗りと上塗りの色を変えるのは、塗り残し（塗りムラ）を目で確認できるようにする実務の知恵です。",
    points: [
      "研磨・パテで面を整えてから塗る（下地が命）",
      "中塗り・上塗りは色を変えて塗り残しを防ぐ",
      "各層は下層が硬化乾燥してから重ねる",
    ],
    glossary: [
      {
        term: "パテしごき",
        meaning:
          "パテを全面にへら付けし、余分をしごき取って素地が見えるまで平滑にする下地調整。",
      },
    ],
  },

  "18.1.5": {
    plainSummary:
      "仕上げの色合はあらかじめ監督職員に提出した見本帳または見本塗板による。",
  },

  "18.1.6": {
    plainSummary:
      "気温5℃以下・湿度85%以上・結露等で乾燥に不適当な場合は塗装しない（採暖・換気を適切に行う場合を除く）。外部は降雨のおそれ・強風時は原則行わない。塗装箇所周辺は養生。溶剤中毒・火災（自然発火）に注意する。",
    why: "塗料は適切な温度・湿度でないと乾燥・硬化不良を起こし、つやが引けたり密着不良になります。「5℃以下・湿度85%以上」は塗装の中止基準として頻出。塗料を拭いた布は放置すると酸化熱で自然発火するため、必ず処理します。",
    points: [
      "5℃以下・湿度85%以上・結露は塗装中止",
      "塗料の付いた布は自然発火のおそれ→作業後直ちに処理",
    ],
    diagrams: [
      {
        svg: SVG_CONDITION,
        caption: "塗装を行わない条件（18.1.6）",
      },
    ],
  },

  "18.1.7": {
    plainSummary:
      "仕上がり面の確認は目視（表18.1.1：見本塗板との比較、むら・しわ・へこみ・はじき・つぶがないこと）。錆止め塗料塗りは塗付け量または標準膜厚を確認（現場塗装は使用量から推定、工場塗装は膜厚計で測定）。",
    why: "錆止めは膜厚が薄いと錆が進むため、目視だけでなく塗付け量・膜厚で管理します。工場塗装では電磁膜厚計で数値確認するのがポイントです。",
  },

  "18.2.1": {
    plainSummary:
      "この節は木部・鉄鋼面・亜鉛めっき鋼面・モルタル面・コンクリート面・ボード面等の素地ごしらえに適用する。",
    glossary: [
      {
        term: "素地ごしらえ",
        meaning:
          "塗装前に下地（素地）の汚れ・油・錆・凹凸を除去して整える下地処理。塗装品質を左右する。",
      },
    ],
  },

  "18.2.2": {
    plainSummary:
      "木部の素地ごしらえは表18.2.1（特記がなければ不透明塗料はA種・透明塗料はB種）。汚れ・付着物除去→やに処理→研磨紙ずり→節止め→穴埋め→研磨紙ずりの工程。透明塗料で色むら等があれば着色剤で色むら直し。",
    why: "木は「やに（樹脂）」と「節」が塗装の敵。やにが出ると塗膜が変色・剥離するので、削り取り・電気ごて焼きで処理し、節にはニスで止めます（節止め）。透明塗料は木目を見せるので、色むら直しが仕上がりを左右します。",
    glossary: [
      {
        term: "節止め",
        meaning:
          "木の節から出るやにやアクが塗膜に染み出すのを、ニス等で止める処理。",
      },
    ],
  },

  "18.2.3": {
    plainSummary:
      "鉄鋼面の素地ごしらえは表18.2.2（特記がなければC種、耐候性塗料の場合はB種）。汚れ・付着物除去→油類除去→錆落し→（A種は化成皮膜処理）。A種・B種は製作工場で行う。",
    why: "鉄の塗装で最重要なのが錆と油の除去。錆や油が残ると、その上にいくら塗っても剥がれます。工場ではブラスト（研掃材の吹付け）で徹底的に錆を落とすA種・B種、現場ではディスクサンダー等のC種、と処理レベルが分かれます。",
    glossary: [
      {
        term: "ブラスト",
        meaning:
          "研削材を高速で吹き付けて錆・黒皮を除去する方法。鉄の下地処理として最も確実。",
      },
    ],
  },

  "18.2.4": {
    plainSummary:
      "亜鉛めっき鋼面の素地ごしらえは表18.2.3（特記がなければ塗り工法に応じた節の規定）。汚れ・付着物除去→油類除去→（A種は化成皮膜処理）。A種は製造所で行う。",
    why: "亜鉛めっき面はツルツルで塗料が付きにくいため、化成皮膜処理（りん酸塩処理等）で塗料の食いつく面を作ります。処理せずに塗ると剥離します。",
  },

  "18.2.5": {
    plainSummary:
      "モルタル面・せっこうプラスター面の素地ごしらえは表18.2.4（特記がなければB種）。乾燥→汚れ除去→吸込止め（シーラー）→穴埋め・パテかい→研磨紙ずり→（A種はパテしごき→研磨紙ずり）。",
    why: "モルタルは水分を吸うので、乾燥とシーラー（吸込止め）が重要。乾いていないと塗膜が膨れ、シーラーがないと塗料が吸い込まれてムラになります。",
  },

  "18.2.6": {
    plainSummary:
      "コンクリート面・ALCパネル面の素地ごしらえは表18.2.5（特記がなければB種）。乾燥→汚れ除去→吸込止め→下地調整塗り→研磨紙ずり→（A種はパテしごき等）。押出成形セメント板面・耐候性塗料の場合は表18.2.6による。",
  },

  "18.2.7": {
    plainSummary:
      "せっこうボード面・その他ボード面の素地ごしらえは表18.2.7（特記がなければB種）。乾燥→汚れ除去→穴埋め・パテかい（釘頭・継目をジョイントコンパウンド等で処理）→研磨紙ずり→（A種はパテしごき等）。",
    why: "ボードは継目と釘頭の処理が命。ジョイントコンパウンドで継目を平滑にし、釘頭のへこみを埋めないと、塗装後にそれらが目立ってしまいます。",
  },

  "18.3.1": {
    plainSummary: "この節は鉄鋼面・亜鉛めっき鋼面の錆止め塗料塗りに適用する。",
    why: "錆止めは鉄部塗装の土台。この上に仕上げ塗装を重ねます。錆止めが不十分だと、どんな仕上げをしても数年で錆が浮いてきます。",
  },

  "18.3.2": {
    plainSummary:
      "鉄鋼面の錆止め塗料の種別は表18.3.1（As種＝鉛・クロムフリーさび止めペイント1種等、適用する節で使い分け）。亜鉛めっき鋼面は表18.3.2（Az種＝一液形変性エポキシ樹脂さび止めペイント等）。",
    glossary: [
      {
        term: "鉛・クロムフリーさび止めペイント",
        meaning:
          "有害な鉛・クロムを含まない環境配慮型の錆止め塗料。現在の標準（As種）。",
      },
      {
        term: "ジンクリッチプライマー",
        meaning:
          "亜鉛末を多く含む錆止め下塗り塗料。犠牲防食で強力に錆を防ぐ（Cs種）。",
      },
    ],
  },

  "18.3.3": {
    plainSummary:
      "鉄鋼面の錆止め塗料塗りは表18.3.3（特記がなければ見え掛りA種・見え隠れB種で2回塗り）。鉄骨等は1回目を製作工場で組立後に塗り（組立後困難な部分は組立前に2回）、現場建て方後に損傷部・接合部の未塗装部を補修（未塗装部は2回塗り）。",
    why: "鉄骨の錆止めは「工場で1回、現場で1回」が基本。ボルト接合部やスパッター付着部は錆びやすいので、現場で確実に2回塗ります。溶接する箇所を先に塗ると溶接不良や有害ガスの原因になるため、溶接後に塗るのが鉄則です。",
    points: [
      "鉄骨は工場で1回目、現場で損傷部・接合部を補修",
      "溶接箇所は溶接後に錆止めを塗る",
      "見え掛りA種（2回＋研磨）・見え隠れB種",
    ],
    diagrams: [
      {
        svg: SVG_RUST,
        caption: "鉄鋼面の錆止め塗料塗り（18.3）",
      },
    ],
  },

  "18.4.1": {
    plainSummary:
      "この節は木部・鉄鋼面・亜鉛めっき鋼面の合成樹脂調合ペイント塗り（SOP）に適用する。",
    glossary: [
      {
        term: "合成樹脂調合ペイント（SOP）",
        meaning:
          "油性の一般的な塗料。鉄部・木部に広く使われる。乾きは遅いが扱いやすい。",
      },
    ],
    diagrams: [
      {
        svg: SVG_PAINT_TYPES,
        caption: "主な塗料の種類と使い分け（18章各節）",
      },
    ],
  },

  "18.4.2": {
    plainSummary:
      "木部のSOP塗りは表18.4.1（特記がなければ屋外A種・屋内B種）。素地ごしらえ→下塗り（木部下塗り用調合ペイント）→（B種はパテかい・研磨）→中塗り→上塗り。木口部分は特に丁寧に。",
    why: "木口（木材の切断面）は水を吸いやすく塗料も吸い込むので、特に丁寧に塗ります。木口の塗装不良は木材腐朽の入り口になります。",
  },

  "18.4.3": {
    plainSummary:
      "鉄鋼面のSOP塗りは表18.4.2（特記がなければB種）。錆止め塗料塗り→中塗り1回目→（A種は研磨・中塗り2回目）→上塗り。",
  },

  "18.4.4": {
    plainSummary:
      "亜鉛めっき鋼面のSOP塗りは表18.4.3。錆止め塗料塗り→中塗り→上塗り。",
  },

  "18.5.1": {
    plainSummary: "この節は屋内の木部のクリヤラッカー塗り（CL）に適用する。",
    glossary: [
      {
        term: "クリヤラッカー（CL）",
        meaning:
          "木目を活かす透明の速乾塗料。屋内の木部の仕上げに使う。屋外には不向き。",
      },
    ],
  },

  "18.5.2": {
    plainSummary:
      "クリヤラッカー塗りは表18.5.1（特記がなければB種）。素地ごしらえ→（A種は目止め・着色）→下塗り（ウッドシーラー）→中塗り（サンジングシーラー）→研磨→上塗り。",
    why: "透明塗装は木の導管（穴）を目止め剤で埋めて平滑にし、着色で色を整えてから透明塗料を重ねます。木目の美しさを見せる仕上げなので、下地の色むら直しが特に重要です。",
  },

  "18.6.1": {
    plainSummary:
      "この節は屋内のコンクリート面・モルタル面等のアクリル樹脂系非水分散形塗料塗り（NAD）に適用する。",
    glossary: [
      {
        term: "アクリル樹脂系非水分散形塗料（NAD）",
        meaning:
          "有機溶剤に樹脂を分散させた塗料。アルカリに強く、コンクリート・モルタルの屋内塗装に使う。",
      },
    ],
  },

  "18.6.2": {
    plainSummary:
      "NAD塗りは表18.6.1（特記がなければB種）。素地ごしらえ（B種）→下塗り→（A種は研磨・中塗り）→上塗り。",
  },

  "18.7.1": {
    plainSummary:
      "この節は屋外の鉄鋼面・亜鉛めっき鋼面・コンクリート面等の耐候性塗料塗り（DP）に適用する。",
    glossary: [
      {
        term: "耐候性塗料（DP）",
        meaning:
          "紫外線・雨に強く色あせしにくい塗料。ふっ素樹脂・シリコーン樹脂・ポリウレタン等がある。屋外の長寿命仕上げ。",
      },
    ],
  },

  "18.7.2": {
    plainSummary:
      "鉄鋼面の耐候性塗料塗りは表18.7.1（上塗り塗料の等級は特記）。錆止め塗料塗り→研磨紙ずり→中塗り→上塗り（鋼構造物用耐候性塗料）。工場で工程3まで行う場合は研磨紙ずりを省略。",
  },

  "18.7.3": {
    plainSummary:
      "亜鉛めっき鋼面の耐候性塗料塗りは表18.7.2。錆止め塗料塗り→研磨紙ずり→中塗り→上塗り。",
  },

  "18.7.4": {
    plainSummary:
      "コンクリート面・押出成形セメント板面の耐候性塗料塗りは表18.7.3（A種はふっ素樹脂、B種はシリコーン樹脂、C種はポリウレタン）。下塗り→中塗り→上塗り（建築用耐候性上塗り塗料、1級ふっ素・2級シリコーン・3級ポリウレタン）。",
    why: "耐候性は樹脂の種類で決まります。ふっ素（1級）＞シリコーン（2級）＞ポリウレタン（3級）の順で耐久性が高く、価格も高い。外壁の改修周期を延ばしたいほど上位グレードを選びます。",
    points: [
      "耐久性の序列：ふっ素（1級）＞シリコーン（2級）＞ポリウレタン（3級）",
    ],
  },

  "18.8.1": {
    plainSummary:
      "この節はコンクリート面・モルタル面・ボード面等および屋内の木部・鉄鋼面・亜鉛めっき鋼面のつや有合成樹脂エマルションペイント塗り（EP-G）に適用する。",
    glossary: [
      {
        term: "つや有合成樹脂エマルションペイント（EP-G）",
        meaning:
          "水性でつやのある塗料。臭気が少なく扱いやすい。屋内の壁・天井・鉄部・木部に広く使う。",
      },
    ],
  },

  "18.8.2": {
    plainSummary:
      "コンクリート・ボード面等のEP-G塗りは表18.8.1（特記がなければB種）。素地ごしらえ→下塗り（シーラー）→中塗り1回目→（A種は研磨・中塗り2回目）→上塗り。天井等の見上げ部分は工程3を省略。",
  },

  "18.8.3": {
    plainSummary:
      "屋内の木部のEP-G塗りは表18.8.2。素地ごしらえ→下塗り→パテかい→研磨→中塗り→上塗り。木口部分は特に丁寧に。",
  },

  "18.8.4": {
    plainSummary:
      "屋内の鉄鋼面のEP-G塗りは表18.8.3（特記がなければB種）。錆止め塗料塗り→中塗り1回目→（A種は研磨・中塗り2回目）→上塗り。",
  },

  "18.8.5": {
    plainSummary:
      "屋内の亜鉛めっき鋼面のEP-G塗りは表18.8.4。錆止め塗料塗り→中塗り→上塗り。",
  },

  "18.9.1": {
    plainSummary:
      "この節はコンクリート面・モルタル面・ボード面等の合成樹脂エマルションペイント塗り（EP）に適用する。",
    glossary: [
      {
        term: "合成樹脂エマルションペイント（EP）",
        meaning:
          "水性のつや消し塗料。内装の壁・天井の最も一般的な塗料。安価で扱いやすい。",
      },
    ],
  },

  "18.9.2": {
    plainSummary:
      "EP塗りは表18.9.1（特記がなければB種）。素地ごしらえ→下塗り（シーラー）→中塗り1回目→（A種は研磨・中塗り2回目）→上塗り。天井等の見上げ部分は工程3を省略。",
    why: "EPは内装の定番塗料。天井の見上げ部分で研磨（工程3）を省くのは、天井は近くで見られず、研磨の手間ほどの効果がないためです。",
  },

  "18.10.1": {
    plainSummary: "この節は屋内の木部のウレタン樹脂ワニス塗り（UC）に適用する。",
    glossary: [
      {
        term: "ウレタン樹脂ワニス（UC）",
        meaning:
          "木目を活かす透明塗料。クリヤラッカーより硬く耐摩耗・耐水性に優れる。床や家具に使う。",
      },
    ],
  },

  "18.10.2": {
    plainSummary:
      "ウレタン樹脂ワニス塗りは表18.10.1（特記がなければB種）。素地ごしらえ→着色→下塗り→研磨→（A種は中塗り・研磨）→上塗り。1液形・2液形がある。",
  },

  "18.11.1": {
    plainSummary: "この節は屋内の木部のピグメントステイン塗りに適用する。",
    glossary: [
      {
        term: "ピグメントステイン",
        meaning:
          "顔料で木を着色する塗料。木目を残しながら色をつける。上に透明塗装を重ねることが多い。",
      },
    ],
  },

  "18.11.2": {
    plainSummary:
      "ピグメントステイン塗りは表18.11.1。素地ごしらえ（B種）→着色→着色むら直し。塗付け量は製造所の仕様による。",
  },

  "18.12.1": {
    plainSummary: "この節は屋外の木部の木材保護塗料塗り（WP）に適用する。",
    glossary: [
      {
        term: "木材保護塗料（WP）",
        meaning:
          "木に浸透して防腐・防虫・撥水・紫外線保護を行う塗料。屋外の木部（ウッドデッキ・ルーバー等）に使う。",
      },
    ],
  },

  "18.12.2": {
    plainSummary:
      "木材保護塗料塗りは表18.12.1（特記がなければB種）。素地ごしらえ→下塗り→上塗り1回目→（A種は上塗り2回目）。",
    why: "木材保護塗料は塗膜を作らず木に浸み込んで守るタイプ。屋外の木は紫外線と水で急速に劣化するため、定期的な塗り直しが前提の仕上げです。",
  },
};

export default ch18;
